import React from 'react';
import { Tag as TagIcon, X } from 'lucide-react';
import TagSelector from './TagSelector.tsx';
import { TagResponse } from '../services/apiTags.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface ContactTagsPanelProps {
  contactTags: TagResponse[];
  tagsLoading: boolean;
  clinicId: number;
  onUpdateTags: (tagIds: number[]) => void;
}

const ContactTagsPanel: React.FC<ContactTagsPanelProps> = ({
  contactTags,
  tagsLoading,
  clinicId,
  onUpdateTags,
}) => {
  const { isDark } = useTheme();

  const handleRemove = (tagId: number) => {
    onUpdateTags(contactTags.filter((t) => t.id !== tagId).map((t) => t.id));
  };

  return (
    <div
      className={`px-4 py-2.5 border-b flex items-center gap-2 flex-wrap ${
        isDark ? 'border-gray-700 bg-[#111827]' : 'border-gray-100 bg-white'
      }`}
    >
      <TagIcon
        className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
        aria-hidden="true"
      />

      {tagsLoading ? (
        <>
          <div className={`w-16 h-5 rounded-full animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`} />
          <div className={`w-20 h-5 rounded-full animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`} />
        </>
      ) : (
        <>
          {contactTags.map((tag) => (
            <span
              key={tag.id}
              className="group inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium leading-none"
              style={{
                backgroundColor: tag.color + (isDark ? '25' : '15'),
                color: tag.color,
                border: `1px solid ${tag.color}40`,
              }}
            >
              <span className="py-0.5">{tag.name}</span>
              <button
                type="button"
                onClick={() => handleRemove(tag.id)}
                title={`Remover ${tag.name}`}
                aria-label={`Remover tag ${tag.name}`}
                className="rounded-full p-0.5 opacity-50 group-hover:opacity-100 transition-opacity hover:bg-black/10 focus:opacity-100 focus:outline-none"
                style={{ color: tag.color }}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          <TagSelector
            selectedTags={contactTags.map((t) => t.id)}
            onTagsChange={onUpdateTags}
            clinicId={clinicId}
            placeholder={contactTags.length === 0 ? '+ Adicionar tag' : '+'}
            maxTags={10}
          />
        </>
      )}
    </div>
  );
};

export default ContactTagsPanel;
